import { CSSObject } from "@emotion/react";
import { LoadingShimmer } from "@newsela/angelou";

import {
  DoubleBarData,
  DoubleBarLabelPlacementOptions,
} from "@/components/DataInfoCard/types";
import getCommaSeparatedNumericString from "@/utils/getCommaSeparatedNumericString";
import getPercentValue from "@/utils/getPercentValue";
import {
  widgetContainerCss,
  valueCss,
  labelCss,
  topLeftGridCss,
  topRightGridCss,
  labelOneCss,
  labelTwoCss,
  topCss,
  barOneCss,
  barTwoCss,
  barCss,
  loadingRootCss,
} from "./DoubleBars.style";

interface DoubleBarProps {
  doubleBarData:
    | {
        barOne: DoubleBarData;
        barTwo: DoubleBarData;
      }
    | undefined;
  additionalStyles?:
    | {
        containerCss?: CSSObject | undefined;
        statValueCss?: CSSObject | undefined;
        statValueAddOnCss?: CSSObject | undefined;
        statValueNumberCss?: CSSObject | undefined;
        loadingBarOneBgColorCss?: string | undefined;
        loadingBarOneShimmerColorCss?: string | undefined;
        loadingBarTwoBgColorCss?: string | undefined;
      }
    | undefined;
  loading?: boolean | undefined;
}

const DoubleBars = ({
  doubleBarData,
  additionalStyles,
  loading,
}: DoubleBarProps) => {
  const barOneValue = doubleBarData?.barOne.value ?? 0;
  const barTwoValue = doubleBarData?.barTwo.value ?? 0;
  const highestValue = Math.max(barOneValue, barTwoValue);
  /**
   * Takes only 80% of the percent value as the card bar height can only max up to 60%
   */
  const calculateBarHeight = (barValue: number) => {
    return barValue === 0
      ? barValue
      : getPercentValue(barValue, highestValue) * 0.8;
  };

  const heightOfBarOne = calculateBarHeight(barOneValue);
  const heightOfBarTwo = calculateBarHeight(barTwoValue);
  const barOneLabelPlacement =
    doubleBarData?.barOne.labelPlacement ??
    DoubleBarLabelPlacementOptions.bottom;
  const barTwoLabelPlacement =
    doubleBarData?.barTwo.labelPlacement ??
    DoubleBarLabelPlacementOptions.bottom;
  const barOneLabel = (
    <div css={[labelCss, labelOneCss]}>{doubleBarData?.barOne.label}</div>
  );
  const barTwoLabel = (
    <div css={[labelCss, labelTwoCss]}>{doubleBarData?.barTwo.label}</div>
  );

  const loadingTwoBars = (
    <div
      css={[
        widgetContainerCss(barOneLabelPlacement),
        additionalStyles?.containerCss,
      ]}
      data-testid="loading-double-bars"
    >
      <div css={[topLeftGridCss, topCss]}>
        {barOneLabelPlacement === DoubleBarLabelPlacementOptions.top &&
          barOneLabel}
        <LoadingShimmer
          backgroundColor={additionalStyles?.loadingBarOneBgColorCss || ""}
          shimmerColor={additionalStyles?.loadingBarOneShimmerColorCss || ""}
          __cssFor={{
            root: loadingRootCss,
          }}
        />
      </div>
      <div css={[topRightGridCss, topCss]}>
        {barTwoLabelPlacement === DoubleBarLabelPlacementOptions.top &&
          barTwoLabel}
        <LoadingShimmer
          backgroundColor={additionalStyles?.loadingBarTwoBgColorCss || ""}
          shimmerColor={additionalStyles?.loadingBarOneShimmerColorCss || ""}
          __cssFor={{
            root: loadingRootCss,
          }}
        />
      </div>
    </div>
  );

  if (loading) {
    return loadingTwoBars;
  }
  return (
    <div
      css={[
        widgetContainerCss(barOneLabelPlacement),
        additionalStyles?.containerCss,
      ]}
    >
      {/* top-left */}
      <div css={[topLeftGridCss, topCss]}>
        <div css={[valueCss, additionalStyles?.statValueNumberCss]}>
          {getCommaSeparatedNumericString(barOneValue)}
        </div>
        {barOneLabelPlacement === DoubleBarLabelPlacementOptions.top &&
          barOneLabel}
        <div
          css={[
            barCss,
            barOneCss(heightOfBarOne),
            doubleBarData?.barOne?.style,
          ]}
        />
      </div>
      {/* bottom-left */}
      {barOneLabelPlacement === DoubleBarLabelPlacementOptions.bottom &&
        barOneLabel}
      {/* top-right */}
      <div css={[topRightGridCss, topCss]}>
        <div css={[valueCss, additionalStyles?.statValueNumberCss]}>
          {getCommaSeparatedNumericString(barTwoValue)}
        </div>
        {barTwoLabelPlacement === DoubleBarLabelPlacementOptions.top &&
          barTwoLabel}
        <div
          css={[
            barCss,
            barTwoCss(heightOfBarTwo),
            doubleBarData?.barTwo?.style,
          ]}
        />
      </div>
      {/* bottom-right */}
      {barTwoLabelPlacement === DoubleBarLabelPlacementOptions.bottom &&
        barTwoLabel}
    </div>
  );
};

export default DoubleBars;
